import { db } from '../components/Dexie';
import { log } from './loggingService';

/**
 * Synchronizes only attributes and their values.
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const syncAttributes = async () => {
  try {
    log("Avvio sincronizzazione completa attributi...", 'INFO');

    // Svuota tabelle locali per un reset completo
    await db.attributi.clear();
    await db.attributiValori.clear();
    log("Tabelle attributi e valori svuotate localmente.", 'INFO');

    // Sync Attributi
    const attrResponse = await fetch('https://deadapi.techysumo.it/techyshop/searchAttributo', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({}), 
    });
    const attrResult = await attrResponse.json();
    
    if (attrResult.result && Array.isArray(attrResult.details)) {
      await db.attributi.bulkAdd(attrResult.details);
      log(`Sincronizzati ${attrResult.details.length} attributi.`, 'INFO');
    }

    // Sync Valori
    const valResponse = await fetch('https://deadapi.techysumo.it/techyshop/searchAttributoValore', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({}),
    });
    const valResult = await valResponse.json();
    if (valResult.result && Array.isArray(valResult.details)) {
      await db.attributiValori.bulkAdd(valResult.details);
      log(`Sincronizzati ${valResult.details.length} valori attributi.`, 'INFO');
    }

    return { success: true, message: 'Attributi e valori sincronizzati con successo!' };
  } catch (error) {
    log("Errore durante la sincronizzazione degli attributi:", 'ERROR', error);
    return { success: false, message: `Errore: ${error.message}` };
  }
};

/**
 * Synchronizes categories with the remote API.
 * @param {boolean} force - If true, clears local data and forces a full sync.
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const syncCategories = async (force = true) => {
  try {
    if (force) {
      log("Reset categorie e associazioni locali in corso...", 'INFO');
      await db.categories.clear();
      await db.techyshop_prodotto_categoria.clear();
    }
    
    log("Avvio download categorie dal server...", 'INFO');

    // Sync Categorie
    const response = await fetch('https://deadapi.techysumo.it/techyshop/searchCategoria', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({})
    });
    
    const result = await response.json();
    
    if (result.result && Array.isArray(result.details)) {
      // Normalizzazione campo visibile e id
      const normalizedCategories = result.details.map(cat => ({
        ...cat,
        id: parseInt(cat.id),
        visibile: (cat.visibile == 1 || cat.visibile === true || String(cat.visibile).toLowerCase() === 'true') ? 1 : 0
      }));
      
      await db.categories.clear();
      await db.categories.bulkAdd(normalizedCategories);
      log(`Sincronizzate ${normalizedCategories.length} categorie.`, 'INFO');

      // Sync Associazioni Prodotto-Categoria
      log("Avvio download associazioni prodotto-categoria...", 'INFO');
      const assocResponse = await fetch('https://deadapi.techysumo.it/techyshop/searchCategoriaProdotto', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify({})
      });
      const assocResult = await assocResponse.json();
      if (assocResult.result && Array.isArray(assocResult.details)) {
        const normalizedAssoc = assocResult.details.map(assoc => ({
          prodotto_id: parseInt(assoc.prodotto_id),
          categoria_id: parseInt(assoc.categoria_id)
        }));
        await db.techyshop_prodotto_categoria.clear();
        await db.techyshop_prodotto_categoria.bulkAdd(normalizedAssoc);
        log(`Sincronizzate ${normalizedAssoc.length} associazioni prodotto-categoria.`, 'INFO');
      }

      return { success: true, message: `Sincronizzate ${normalizedCategories.length} categorie con successo!` };
    } else {
      throw new Error(result.text || "Errore durante il download delle categorie.");
    }
  } catch (error) {
    log("Errore durante la sincronizzazione delle categorie:", 'ERROR', error);
    return { success: false, message: `Errore: ${error.message}` };
  }
};

/**
 * Synchronizes the association between products and attributes.
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const syncProductAttributes = async () => {
  try {
    log("Avvio sincronizzazione associazioni prodotti-attributi...", 'INFO');

    // Svuota tabella locale
    await db.productAttributes.clear();
    log("Tabella associazioni prodotti-attributi svuotata.", 'INFO');

    const response = await fetch('https://deadapi.techysumo.it/techyshop/searchAttributoProdotto', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({}),
    });

    const result = await response.json();

    if (result.result && Array.isArray(result.details)) {
      // Mappatura dei dati per Dexie se necessario
      // Ci aspettiamo: prodotto_id, name (o nome_attributo), value (o valore)
      const normalizedAttributes = result.details.map(attr => ({
        prodotto_id: parseInt(attr.prodotto_id),
        name: attr.name || attr.nome_attributo || attr.attributo_nome || '',
        value: attr.value || attr.valore || attr.valore_nome || ''
      }));

      await db.productAttributes.bulkAdd(normalizedAttributes);
      log(`Sincronizzate ${normalizedAttributes.length} associazioni attributi-prodotto.`, 'INFO');
      return { success: true, message: `Sincronizzate ${normalizedAttributes.length} associazioni con successo!` };
    } else {
      throw new Error(result.text || "Errore durante il download delle associazioni attributi.");
    }
  } catch (error) {
    log("Errore durante la sincronizzazione degli attributi prodotto:", 'ERROR', error);
    return { success: false, message: `Errore: ${error.message}` };
  }
};

/**
 * Refreshes attributes for a specific product by downloading them from the server.
 * @param {number|string} productId - The ID of the product.
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const refreshProductAttributes = async (productId) => {
  try {
    log(`Refresh attributi per prodotto ${productId}...`, 'INFO');

    // 1. Cancella attributi esistenti per questo prodotto da Dexie
    await db.productAttributes.where('prodotto_id').equals(parseInt(productId)).delete();

    // 2. Scarica tutti gli attributi (senza parametri)
    const attrResponse = await fetch('https://deadapi.techysumo.it/techyshop/searchAttributo', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({}),
    });
    const attrResult = await attrResponse.json();
    if (attrResult.result && Array.isArray(attrResult.details)) {
      await db.attributi.bulkPut(attrResult.details);
    }

    // 3. Scarica tutti i valori attributo (senza parametri)
    const valResponse = await fetch('https://deadapi.techysumo.it/techyshop/searchAttributoValore', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({}),
    });
    const valResult = await valResponse.json();
    if (valResult.result && Array.isArray(valResult.details)) {
      await db.attributiValori.bulkPut(valResult.details);
    }

    // 4. Scarica attributi specifici per il prodotto
    const prodAttrResponse = await fetch('https://deadapi.techysumo.it/techyshop/searchAttributoProdotto', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({ prodotto_id: productId }),
    });
    const prodAttrResult = await prodAttrResponse.json();
    if (prodAttrResult.result && Array.isArray(prodAttrResult.details)) {
      const normalizedAttributes = prodAttrResult.details.map(attr => ({
        prodotto_id: parseInt(attr.prodotto_id),
        name: attr.name || attr.nome_attributo || attr.attributo_nome || '',
        value: attr.value || attr.valore || attr.valore_nome || ''
      }));
      await db.productAttributes.bulkAdd(normalizedAttributes);
    }

    return { success: true, message: 'Attributi sincronizzati con successo!' };
  } catch (error) {
    log(`Errore refreshProductAttributes per prodotto ${productId}:`, 'ERROR', error);
    return { success: false, message: `Errore: ${error.message}` };
  }
};

/**
 * Refreshes categories for a specific product by downloading them from the server.
 * @param {number|string} productId - The ID of the product.
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const refreshProductCategories = async (productId) => {
  try {
    log(`Refresh categorie per prodotto ${productId}...`, 'INFO');

    // 1. Cancella associazioni esistenti per questo prodotto da Dexie
    await db.techyshop_prodotto_categoria.where('prodotto_id').equals(parseInt(productId)).delete();

    // 2. Scarica tutte le categorie (senza parametri)
    const catResponse = await fetch('https://deadapi.techysumo.it/techyshop/searchCategoria', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({}),
    });
    const catResult = await catResponse.json();
    if (catResult.result && Array.isArray(catResult.details)) {
      const normalizedCategories = catResult.details.map(cat => ({
        ...cat,
        id: parseInt(cat.id),
        visibile: (cat.visibile == 1 || cat.visibile === true || String(cat.visibile).toLowerCase() === 'true') ? 1 : 0
      }));
      await db.categories.bulkPut(normalizedCategories);
    }

    // 3. Scarica associazioni specifiche per il prodotto
    const assocResponse = await fetch('https://deadapi.techysumo.it/techyshop/searchCategoriaProdotto', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({ prodotto_id: productId }),
    });
    const assocResult = await assocResponse.json();
    if (assocResult.result && Array.isArray(assocResult.details)) {
      const normalizedAssoc = assocResult.details.map(assoc => ({
        prodotto_id: parseInt(assoc.prodotto_id),
        categoria_id: parseInt(assoc.categoria_id)
      }));
      await db.techyshop_prodotto_categoria.bulkPut(normalizedAssoc);
    }

    return { success: true, message: 'Categorie sincronizzate con successo!' };
  } catch (error) {
    log(`Errore refreshProductCategories per prodotto ${productId}:`, 'ERROR', error);
    return { success: false, message: `Errore: ${error.message}` };
  }
};

/**
 * Synchronizes products and attributes with the remote API.
 * @param {boolean} force - If true, clears local data and forces a full sync.
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const syncProducts = async (force = false) => {
  try {
    let lastUpdate = await db.genericTable.get({ table: 'product', field: 'lastupdateproduct' });
    
    if (force) {
      log("Forzatura aggiornamento catalogo: eliminazione dati locali...", 'INFO');
      await db.products.clear();
      lastUpdate = null;
    }

    log(force ? "Avvio download completo catalogo..." : "Verifica nuovi prodotti in corso...", 'INFO');

    const body = {
      getUpdateItem: true,
      limit: 0,
      lastupdateproduct: (lastUpdate && lastUpdate.value) ? lastUpdate.value : false
    };

    const response = await fetch('https://deadapi.techysumo.it/techyshop/searchItem', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify(body),
    });

    const result = await response.json();

    if (result.result) {
      if (Array.isArray(result.details) && result.details.length > 0) {
        const normalizedProducts = result.details
          .filter(p => p.id)
          .map(p => ({
            ...p,
            id: parseInt(p.id),
            visibile: (p.visibile == 1 || p.visibile === true || String(p.visibile).toLowerCase() === 'true') ? 1 : 0,
            in_evidenza: (p.in_evidenza == 1 || p.in_evidenza === true || String(p.in_evidenza).toLowerCase() === 'true') ? 1 : 0
          }));

        await db.products.bulkPut(normalizedProducts);
        log(`Sincronizzati ${normalizedProducts.length} prodotti.`, 'INFO');
      } else {
        log("Nessun nuovo prodotto da sincronizzare.", 'INFO');
      }

      // Sync Attributi e Valori
      try {
        const attrResponse = await fetch('https://deadapi.techysumo.it/techyshop/getAttributi', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': process.env.REACT_APP_API_KEY,
          }
        });
        const attrResult = await attrResponse.json();
        if (attrResult.result && Array.isArray(attrResult.details)) {
          await db.attributi.bulkPut(attrResult.details);
        }

        const valResponse = await fetch('https://deadapi.techysumo.it/techyshop/getAttributiValori', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': process.env.REACT_APP_API_KEY,
          }
        });
        const valResult = await valResponse.json();
        if (valResult.result && Array.isArray(valResult.details)) {
          await db.attributiValori.bulkPut(valResult.details);
        }
      } catch (e) {
        log("Errore durante il sync degli attributi:", 'WARNING', e);
      }

      // Aggiorna sempre la data di ultimo controllo se l'API ha risposto con successo
      const today = new Date();
      const day = String(today.getDate()).padStart(2, '0');
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const year = today.getFullYear();
      const formattedDate = `${day}/${month}/${year}`;

      await db.genericTable.put({
        table: 'product',
        field: 'lastupdateproduct',
        value: formattedDate,
        date: today.toISOString(),
      });
      
      log(`Sincronizzazione completata: ${formattedDate}`, 'INFO');
      return { success: true, message: force ? 'Catalogo resettato e aggiornato!' : 'Sincronizzazione completata!' };
    } else {
      throw new Error(result.text || "L'API ha restituito un errore.");
    }
  } catch (error) {
    log("Errore durante la sincronizzazione dei prodotti:", 'ERROR', error);
    return { success: false, message: `Errore: ${error.message}` };
  }
};
